import {
  Alert,
  AlertDescriptionProps,
  AlertIndicatorProps,
  AlertRootProps,
  FlexProps,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { MdCheck, MdInfo } from 'react-icons/md';
import { MBox, MFlex } from './chakra';
import MCloseButton from './MCloseButton';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

const getValues = (type: AlertType = 'info') => {
  switch (type) {
    case 'success':
      return {
        bgColor: 'tGreen.light',
        iconColor: 'tGreen.mDarkShade',
        icon: MdCheck,
      };
    case 'error':
      return {
        bgColor: 'tRed.cosmos',
        iconColor: 'tRed.base',
        icon: MdInfo,
      };
    case 'warning':
      return {
        bgColor: 'tCreamsicle.base',
        iconColor: 'tOrange.tangerine',
        icon: MdInfo,
      };
    case 'info':
    default:
      return {
        bgColor: 'tBlue.hover',
        iconColor: 'tIndigo.base',
        icon: MdInfo,
      };
  }
};

interface MAlertProps extends FlexProps {
  type?: AlertType;
  title?: string;
  dismissable?: boolean;
  alertProps?: AlertRootProps;
  alertIconProps?: AlertIndicatorProps;
  alertDescriptionProps?: AlertDescriptionProps;
  omitIcon?: boolean;
  onDismiss?: () => void;
  children: React.ReactNode;
}

export const MAlert = ({
  type = 'info',
  title,
  dismissable = false,
  alertProps,
  alertIconProps,
  alertDescriptionProps,
  omitIcon = false,
  onDismiss,
  children,
  ...rest
}: MAlertProps) => {
  const [dismissed, setDismissed] = useState(false);
  const { bgColor, iconColor } = getValues(type);

  if (dismissed) {
    return null;
  }

  return (
    <MFlex alignItems="flex-end" {...rest}>
      {/* v2's Alert set `role="alert"` by default; v3's root sets no role.
          Before the spread so `alertProps.role` can still override it. */}
      <Alert.Root
        role="alert"
        status={type}
        bgColor={bgColor}
        borderRadius="md"
        {...alertProps}
      >
        <MFlex width={'100%'}>
          {!omitIcon && (
            // A flex box, not a block: v3's indicator is inline-flex, which in
            // a block sits on a text line's baseline and rides above the label.
            <MFlex alignSelf="center" mr={2}>
              <Alert.Indicator color={iconColor} {...alertIconProps} />
            </MFlex>
          )}
          {/* Centred like the icon: the 24px dismiss button makes a one-line
              alert's row taller than its text, and a stretched box left the
              line at the top, 2px above the icon. A multi-line text is the
              tallest item, so it still fills the row. */}
          <MBox width={'100%'} alignSelf="center">
            {title && (
              <Alert.Title fontSize="sm" fontWeight={600} color="fg.primary">
                {title}
              </Alert.Title>
            )}
            <Alert.Description
              fontSize={title ? 'sm' : 'xs'}
              color="fg.primary"
              {...alertDescriptionProps}
            >
              {children}
            </Alert.Description>
          </MBox>
          {dismissable && (
            <MCloseButton
              onClick={() => {
                setDismissed(true);
                onDismiss?.();
              }}
            />
          )}
        </MFlex>
      </Alert.Root>
    </MFlex>
  );
};

export default MAlert;
